import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Form, Button, Row, Col, Spinner, Alert, Card, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../campaigns.css';
import { CampaignService, CampaignTypeService, LanguageService } from '../../../../../Services/Axios/Api';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MediaSelector from '../../../Media/MediaSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faQrcode } from '@fortawesome/free-solid-svg-icons';
import LanguageSelector from '../../../../../Components/LanguageSelector/LanguageSelector';
import { QRCodeSVG } from 'qrcode.react';
import { getTenantFavicon } from '../../../../../Services/bootstrap/tenantBoot';

const safeParseOptions = (optionsString) => {
    if (!optionsString || typeof optionsString !== 'string') return {};
    try {
        const parsed = JSON.parse(optionsString);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const EditCampaign = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        description: '',
        target: '',
        mainPhoto: '',
        mediaFiles: [],
        isRecurring: false,
        recurringMode: 'nextMonth',
        recurringAmount: 0,
        isFixedPrice: false,
        fixedPrice: 0,
        currentAmount: 0,
        isActive: true,
        showQr: false,
        isVisible: true,
    });
    const [campaignId, setCampaignId] = useState(null);
    const [showQuickDonate, setShowQuickDonate] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [selectedTypeMode, setSelectedTypeMode] = useState('');
    const [selectedTypeIsGeneral, setSelectedTypeIsGeneral] = useState(false);
    const [campaignTypes, setCampaignTypes] = useState([]);
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
    const [isAdditionalMediaSelectorOpen, setIsAdditionalMediaSelectorOpen] = useState(false);
    const [currentMediaSelectorType, setCurrentMediaSelectorType] = useState('main');
    
    // Language states
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);
    const quickDonateQrRef = useRef(null);

    useEffect(() => {
        loadLanguages();
    }, []);

    // Load data after languages are loaded and current language is set
    useEffect(() => {
        if (isLanguagesLoaded && currentLanguage) {
            const loadData = async () => {
                await fetchCampaignTypes();
                await fetchCampaignData();
            };
            loadData();
        }
    }, [isLanguagesLoaded, currentLanguage]);

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('EditCampaign: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const fetchCampaignTypes = async () => {
        try {
            const response = await CampaignTypeService.getCampaignTypes();
            setCampaignTypes(response.result);
        } catch (error) {
            toast.error('Failed to fetch campaign types');
            console.error('Error fetching campaign types:', error);
        }
    };

    const fetchCampaignData = async () => {
        try {
            setLoading(true);
            console.log('EditCampaign: Loading data for ID:', id, 'and language:', currentLanguage);
            
            // Use the ByIdAndLanguage endpoint if available, otherwise fallback to regular endpoint
            let response;
            try {
                // Try to use language-specific endpoint first
                response = await CampaignService.getCampaignByIdAndLanguage(id, currentLanguage, true);
            } catch (langError) {
                // Fallback to regular endpoint if language-specific one doesn't exist
                console.log('Language-specific endpoint not available, using regular endpoint');
                response = await CampaignService.getCampaignById(id, true);
            }
            
            const campaign = response.result;
            setCampaignId(campaign?.id ?? null);

            const options = safeParseOptions(campaign?.options);
            
            // Get fresh campaign types in case they weren't loaded yet
            const typesResponse = await CampaignTypeService.getCampaignTypes();
            const selectedType = typesResponse.result.find(type => type.id === campaign.campaignSectionId);
            
            setSelectedTypeMode(selectedType ? selectedType.mode : '');
            setSelectedTypeIsGeneral(selectedType ? selectedType.isGeneralDonation : false);
            setFormData({
                name: campaign.name,
                type: campaign.campaignSectionId,
                description: campaign.description,
                target: campaign.targetAmount??0,
                mainPhoto: campaign.mainImage,
                mediaFiles: campaign.additionalImages ? campaign.additionalImages.split(',') : [],
                isRecurring: campaign.isRecurring || false,
                recurringMode: campaign.recurringMode || 'nextMonth',
                recurringAmount: campaign.recurringAmount || 0,
                isFixedPrice: campaign.isFixedPrice || false,
                fixedPrice: campaign.fixedPrice || 0,
                currentAmount: campaign.currentAmount || 0,
                isActive: typeof campaign.isActive === 'boolean' ? campaign.isActive : true,
                showQr: typeof options.showQr === 'boolean' ? options.showQr : false,
                isVisible: typeof campaign.isVisible === 'boolean' ? campaign.isVisible : true,
            });
            
        } catch (error) {
            console.error('Error fetching campaign:', error);
            toast.error('Failed to fetch campaign data');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // fetchCampaignData will be called automatically by the useEffect when currentLanguage changes
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.type) {
            newErrors.type = 'Type is required';
        }
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }
        if (selectedTypeMode === 'Progressing' && !formData.target) {
            newErrors.target = 'Target is required for progressive campaigns';
        }
        if (!formData.mainPhoto) {
            newErrors.mainPhoto = 'Main photo is required';
        }
        if (formData.isRecurring && !formData.recurringMode) {
            newErrors.recurringMode = 'Please select a recurring mode';
        }
        if (formData.isFixedPrice && (!formData.fixedPrice || formData.fixedPrice <= 0)) {
            newErrors.fixedPrice = 'Valid fixed price is required';
        }
        // No validation for isActive (optional)
        return newErrors;
    };

    const handleTypeChange = (e) => {
        const selectedType = campaignTypes.find(type => type.id == e.target.value);
        setSelectedTypeMode(selectedType ? selectedType.mode : '');
        setSelectedTypeIsGeneral(selectedType ? selectedType.isGeneralDonation : false);
        setFormData({
            ...formData,
            type: e.target.value,
            target: selectedType?.mode === 'Progressing' ? formData.target : '',
            isRecurring: formData.isRecurring
        });
    };

    const handleMediaSelect = (selectedPath) => {
        if (currentMediaSelectorType === 'main') {
            const main = Array.isArray(selectedPath) ? selectedPath[0] : selectedPath;
            setFormData({ ...formData, mainPhoto: main });
        } else {
            const newItems = Array.isArray(selectedPath) ? selectedPath : [selectedPath];
            setFormData(prev => {
                const combined = [...prev.mediaFiles, ...newItems];
                const seen = new Set();
                const deduped = combined.filter((item) => {
                    if (seen.has(item)) return false;
                    seen.add(item);
                    return true;
                });
                return { ...prev, mediaFiles: deduped };
            });
        }
    };

    const handleRemoveAdditional = (index) => {
        setFormData(prev => ({
            ...prev,
            mediaFiles: prev.mediaFiles.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            try {
                setLoading(true);
                setErrors({});
                await CampaignService.updateCampaign(id, {
                    name: formData.name,
                    campaignSectionId: formData.type,
                    description: formData.description,
                    targetAmount: formData.target||0,
                    mainImage: formData.mainPhoto,
                    additionalImages: formData.mediaFiles.join(','),
                    isActive: formData.isActive,
                    options: JSON.stringify({ showQr: !!formData.showQr }),
                    language: currentLanguage,
                    isFixedPrice: formData.isFixedPrice,
                    fixedPrice: formData.fixedPrice || 0,
                    isRecurring: formData.isRecurring,
                    recurringMode: formData.recurringMode,
                    recurringAmount: formData.isFixedPrice ? formData.fixedPrice : 0,
                    isVisible: formData.isVisible
                });
                setShowSuccess(true);
                toast.success('Campaign updated successfully');
                setTimeout(() => {
                    navigate('/bj-admin/donations/campaigns');
                }, 2000);
            } catch (error) {
                toast.error('Failed to update campaign');
                console.error('Error updating campaign:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setErrors(formErrors);
        }
    };

    const handleBack = () => {
        navigate('/bj-admin/donations/campaigns');
    };

    const publicSlug = useMemo(() => String(formData.name || '').replace(/\s+/g, '-'), [formData.name]);
    const quickDonateUrl = useMemo(() => {
        if (!campaignId) return '';
        return `${window.location.origin}/donate/${publicSlug}?id=${campaignId}`;
    }, [campaignId, publicSlug]);

    const handleCopyQuickDonateLink = async () => {
        if (!quickDonateUrl) return;
        try {
            await navigator.clipboard.writeText(quickDonateUrl);
            toast.success('Link copied to clipboard');
        } catch (error) {
            console.error('Clipboard error:', error);
        }
    };

    const handleDownloadQuickDonateQr = () => {
        const svgEl = quickDonateQrRef.current;
        if (!svgEl) return;

        const serialized = new XMLSerializer().serializeToString(svgEl);
        const svgWithDoctype = `<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`;
        const blob = new Blob([svgWithDoctype], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `quick-donate-qr-${campaignId || "campaign"}.svg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    // Utility function to check for video file extensions
    const isVideoFile = (filePath) => {
        return /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(filePath);
    };
    // Utility function to check for image file extensions
    const isImageFile = (filePath) => {
        return /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i.test(filePath);
    };

    if (!isLanguagesLoaded) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <h2 className="mb-4">Edit Campaign</h2>
            
            {/* Language Management Card */}
            <Card className="mb-4 language-management-card">
                <Card.Header className="language-management-header">
                    <Row className="">
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-3">
                                <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                <div>
                                    <h6 className="mb-0">Language Management</h6>
                                    <small className="text-muted">
                                        Editing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="d-flex justify-content-end">
                            <LanguageSelector
                                currentLanguage={currentLanguage}
                                availableLanguages={availableLanguages}
                                onLanguageChange={handleLanguageChange}
                                showBadge={false}
                                showCopyFromLanguage={false}
                            />
                        </Col>
                    </Row>
                </Card.Header>
                
                {/* Language Alert */}
                {showLanguageAlert && (
                    <Card.Body className="p-3 language-alert-container">
                        <Alert
                            variant={languageAlertType} 
                            dismissible 
                            onClose={() => setShowLanguageAlert(false)}
                            className="mb-0"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faGlobe} />
                                <div>
                                    {languageAlertType === 'warning' ? (
                                        <>
                                            <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                            {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                        </>
                                    ) : (
                                        <>
                                            <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this campaign.
                                        </>
                                    )}
                                </div>
                            </div>
                        </Alert>
                    </Card.Body>
                )}
            </Card>

            {showSuccess && (
                <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                    Campaign updated successfully!
                </Alert>
            )}
            <Form onSubmit={handleSubmit} noValidate>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formName">
                            <Form.Label>Campaign Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter campaign name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                isInvalid={!!errors.name}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.name}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formType">
                            <Form.Label>Campaign Type</Form.Label>
                            <Form.Select
                                value={formData.type}
                                onChange={handleTypeChange}
                                isInvalid={!!errors.type}
                            >
                                <option value="">Select campaign type</option>
                                {campaignTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors.type}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                {selectedTypeMode === 'Progressing' && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formTarget">
                                <Form.Label>Target Amount ($)</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter target amount"
                                    value={formData.target}
                                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                                    isInvalid={!!errors.target}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.target}
                                </Form.Control.Feedback>

                            </Form.Group>

                            <Form.Group controlId="formTarget">
                                <Form.Label>Current Amount ($)</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.currentAmount}                                    
                                    disabled={true}
                                />
                               
                                
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter campaign description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                isInvalid={!!errors.description}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.description}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formMainPhoto">
                            <Form.Label>Main Photo</Form.Label>
                            <div className="d-flex align-items-center">
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => {
                                        setCurrentMediaSelectorType('main');
                                        setIsMediaSelectorOpen(true);
                                    }}
                                >
                                    Select Main Photo
                                </Button>
                                {formData.mainPhoto && (
                                    <div className="ms-3">
                                        <div className="media-thumb">
                                            {isVideoFile(formData.mainPhoto) ? (
                                                <video 
                                                    src={formData.mainPhoto} 
                                                    className="media-thumb__media"
                                                    controls 
                                                />
                                            ) : isImageFile(formData.mainPhoto) ? (
                                                <img 
                                                    src={formData.mainPhoto} 
                                                    alt="Main campaign" 
                                                    className="media-thumb__media"
                                                />
                                            ) : (
                                                <iframe 
                                                    src={formData.mainPhoto}
                                                    className="media-thumb__media"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    allowFullScreen
                                                    loading="lazy"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    title="main-media-preview"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {errors.mainPhoto && (
                                <div className="text-danger mt-1">{errors.mainPhoto}</div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formMediaFiles">
                            <Form.Label>Additional Photos/Videos</Form.Label>
                            <div className="d-flex align-items-center">
                                <Button 
                                    variant="outline-primary" 
                                    onClick={() => {
                                        setCurrentMediaSelectorType('additional');
                                        setIsAdditionalMediaSelectorOpen(true);
                                    }}
                                >
                                    Select Additional Media
                                </Button>
                                {formData.mediaFiles.length > 0 && (
                                    <div className="ms-3 media-thumb-grid">
                                        {formData.mediaFiles.map((file, index) => (
                                            <div key={index} className="media-thumb">
                                                {isVideoFile(file) ? (
                                                    <video
                                                        src={file}
                                                        className="media-thumb__media"
                                                        controls
                                                    />
                                                ) : isImageFile(file) ? (
                                                    <img 
                                                        src={file} 
                                                        alt={`Additional ${index + 1}`} 
                                                        className="media-thumb__media"
                                                    />
                                                ) : (
                                                    <iframe 
                                                        src={file}
                                                        className="media-thumb__media"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        allowFullScreen
                                                        loading="lazy"
                                                        referrerPolicy="strict-origin-when-cross-origin"
                                                        title={`additional-media-${index}`}
                                                    />
                                                )}
                                                <button 
                                                    type="button"
                                                    className="media-thumb__remove"
                                                    onClick={() => handleRemoveAdditional(index)}
                                                    aria-label={`Remove additional media ${index + 1}`}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                {!selectedTypeIsGeneral && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formIsActive">
                                <Form.Check
                                    type="switch"
                                    id="isActiveSwitch"
                                    label="Active"
                                    checked={formData.isActive}
                                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formShowQr" className="mt-2 mb-2">
                                <Form.Check
                                    type="switch"
                                    id="showQrSwitch"
                                    label="Show Quick Donate QR on campaign page"
                                    checked={!!formData.showQr}
                                    onChange={e => setFormData({ ...formData, showQr: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formIsVisible" className="mt-2 mb-2">
                                <Form.Check
                                    type="switch"
                                    id="isVisibleSwitch"
                                    label="Show on Website (visible on Home & Campaigns pages)"
                                    checked={formData.isVisible}
                                    onChange={e => setFormData({ ...formData, isVisible: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formFixedPriceToggle" className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="fixedPriceSwitch"
                                    label="Fixed Price Campaign (سعر ثابت)"
                                    checked={formData.isFixedPrice}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData({ 
                                            ...formData, 
                                            isFixedPrice: checked
                                        });
                                    }}
                                />
                            </Form.Group>
                            <Form.Group controlId="formRecurring">
                                <Form.Check
                                    type="checkbox"
                                    label="Make this a recurring campaign"
                                    checked={formData.isRecurring}
                                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}
                {selectedTypeIsGeneral && (
                     <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formIsActive">
                                <Form.Check
                                    type="switch"
                                    id="isActiveSwitch"
                                    label="Active"
                                    checked={formData.isActive}
                                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formShowQr" className="mt-2 mb-2">
                                <Form.Check
                                    type="switch"
                                    id="showQrSwitchGeneral"
                                    label="Show Quick Donate QR on campaign page"
                                    checked={!!formData.showQr}
                                    onChange={e => setFormData({ ...formData, showQr: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formIsVisibleGeneral" className="mt-2 mb-2">
                                <Form.Check
                                    type="switch"
                                    id="isVisibleSwitchGeneral"
                                    label="Show on Website (visible on Home & Campaigns pages)"
                                    checked={formData.isVisible}
                                    onChange={e => setFormData({ ...formData, isVisible: e.target.checked })}
                                />
                            </Form.Group>
                            <Form.Group controlId="formFixedPriceToggleGeneral" className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="fixedPriceSwitchGeneral"
                                    label="Fixed Price Campaign (سعر ثابت)"
                                    checked={formData.isFixedPrice}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFormData({ 
                                            ...formData, 
                                            isFixedPrice: checked
                                        });
                                    }}
                                />
                            </Form.Group>
                            <Form.Group controlId="formRecurringGeneral">
                                <Form.Check
                                    type="checkbox"
                                    label="Make this a recurring campaign"
                                    checked={formData.isRecurring}
                                    onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {formData.isFixedPrice && (
                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="formFixedPrice">
                                <Form.Label>Fixed Price per Item (سعر ثابت)</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter fixed price"
                                    value={formData.fixedPrice}
                                    onChange={(e) => setFormData({ ...formData, fixedPrice: e.target.value })}
                                    isInvalid={!!errors.fixedPrice}
                                    min="1"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.fixedPrice}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                )}

                {formData.isRecurring && (
                    <>
                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="formRecurringMode">
                                    <Form.Label>Recurring Mode</Form.Label>
                                    <div className="border rounded p-3">
                                        <Form.Check
                                            type="radio"
                                            id="nextMonth"
                                            name="recurringMode"
                                            label={
                                                <div>
                                                    <strong>Next Month Mode</strong>
                                                    <p className="text-muted mb-0">
                                                        Subscription will be collected after one month from the subscription date.
                                                        Example: If someone subscribes on February 15th, their next payment will be on March 15th.
                                                    </p>
                                                </div>
                                            }
                                            checked={formData.recurringMode === 'nextMonth'}
                                            onChange={() => setFormData({ ...formData, recurringMode: 'nextMonth' })}
                                            className="mb-3"
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="calendarMonth"
                                            name="recurringMode"
                                            label={
                                                <div>
                                                    <strong>Calendar Month Mode</strong>
                                                    <p className="text-muted mb-0">
                                                        For subscriptions between 1-15: Payment is for current month, next billing on 1st of next month.
                                                        For subscriptions between 16-31: Payment is for next month, next billing on 1st of following month.
                                                        Example: If someone subscribes on February 13th, they pay for February and next billing is March 1st.
                                                        If they subscribe on February 20th, they pay for March and next billing is April 1st.
                                                    </p>
                                                </div>
                                            }
                                            checked={formData.recurringMode === 'calendarMonth'}
                                            onChange={() => setFormData({ ...formData, recurringMode: 'calendarMonth' })}
                                        />
                                    </div>
                                    {errors.recurringMode && (
                                        <div className="text-danger mt-1">{errors.recurringMode}</div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

                <div className="d-flex justify-content-end">
                    <Button
                        variant="outline-primary"
                        className="me-2"
                        type="button"
                        disabled={!quickDonateUrl}
                        onClick={() => setShowQuickDonate(true)}
                        title="Quick Donate QR"
                    >
                        <FontAwesomeIcon icon={faQrcode} className="me-2" />
                        Quick Donate QR
                    </Button>
                    <Button variant="success" className="me-2" type="submit" disabled={loading}>
                        {loading ? (
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                            'Update'
                        )}
                    </Button>
                    <Button variant="danger" onClick={handleBack}>
                        Cancel
                    </Button>
                </div>
            </Form>

            <MediaSelector
                isOpen={isMediaSelectorOpen}
                onClose={() => setIsMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={false}
            />

            <MediaSelector
                isOpen={isAdditionalMediaSelectorOpen}
                onClose={() => setIsAdditionalMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={true}
            />

            <Modal show={showQuickDonate} onHide={() => setShowQuickDonate(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Quick Donate</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column align-items-center gap-3">
                        {quickDonateUrl ? (
                            <QRCodeSVG
                                ref={quickDonateQrRef}
                                value={quickDonateUrl}
                                size={220}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="H"
                                imageSettings={{
                                    src: getTenantFavicon() || '',
                                    height: 40,
                                    width: 40,
                                    excavate: true
                                }}
                            />
                        ) : (
                            <div className="text-muted">Save campaign first to generate QR.</div>
                        )}
                        <div className="text-center small text-muted" style={{ wordBreak: 'break-word' }}>
                            {quickDonateUrl || ''}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={handleDownloadQuickDonateQr} disabled={!quickDonateUrl}>
                        Download
                    </Button>
                    <Button variant="outline-secondary" onClick={handleCopyQuickDonateLink} disabled={!quickDonateUrl}>
                        Copy link
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => quickDonateUrl && window.open(quickDonateUrl, '_blank', 'noopener,noreferrer')}
                        disabled={!quickDonateUrl}
                    >
                        Open
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EditCampaign; 